import { useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React examples
import DataTable from "examples/Tables/DataTable";

// Data
import data from "layouts/dashboard/components/CycleDP/data";
import PropTypes from "prop-types";

function CycleDP({
  avg_temp_dp,
  avg_tempdp_status,
  avg_tempdp_error,
  avg_hum_dp,
  avg_humdp_status,
  avg_humdp_error,
  avg_nh3_dp,
  avg_nh3dp_status,
  avg_nh3dp_error,
  avg_fcr_dp,
  avg_fcrdp_status,
  avg_fcrdp_error,
  avg_mortality_dp,
  avg_mortalitydp_status,
  avg_mortalitydp_error,
  avg_weight_dp,
  avg_weightdp_status,
  avg_weightdp_message,
  avg_weightdp_error,
  avg_wtemp_dp,
  avg_wtempdp_status,
  avg_wtempdp_error,
  avg_lux_dp,
  avg_luxdp_status,
  avg_luxdp_error,
  avg_dark_dp,
  avg_darkdp_status,
  avg_darkdp_error,
  env_dark_reading,
  env_dark_status,
  env_dark_target,
  avg_ftemp_dp,
  avg_ftempdp_status,
  avg_ftempdp_error,
  avg_ph_dp,
  avg_phdp_status,
  avg_act_dp,
  avg_actdp_status,
  avg_actdp_error,
  avg_act_message,
  onMortalityClick,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);

  const openMenu = (event) => setMenuAnchor(event.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const { columns, rows } = data({
    avg_temp_dp,
    avg_tempdp_status,
    avg_tempdp_error,
    avg_hum_dp,
    avg_humdp_status,
    avg_humdp_error,
    avg_nh3_dp,
    avg_nh3dp_status,
    avg_nh3dp_error,
    avg_fcr_dp,
    avg_fcrdp_status,
    avg_fcrdp_error,
    avg_mortality_dp,
    avg_mortalitydp_status,
    avg_mortalitydp_error,
    avg_weight_dp,
    avg_weightdp_status,
    avg_weightdp_message,
    avg_weightdp_error,
    avg_wtemp_dp,
    avg_wtempdp_status,
    avg_wtempdp_error,
    avg_lux_dp,
    avg_luxdp_status,
    avg_luxdp_error,
    avg_dark_dp,
    avg_darkdp_status,
    avg_darkdp_error,
    env_dark_reading,
    env_dark_status,
    env_dark_target,
    avg_ftemp_dp,
    avg_ftempdp_status,
    avg_ftempdp_error,
    avg_ph_dp,
    avg_phdp_status,
    avg_act_dp,
    avg_actdp_status,
    avg_actdp_error,
    avg_act_message,
    onMortalityClick,
  });

  return (
    <Card>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
        <MDTypography variant="h6">Cycle Performance</MDTypography>
        <MDBox color="text" px={2}>
          <Icon sx={{ cursor: "pointer", fontWeight: "bold" }} fontSize="small" onClick={openMenu}>
            more_vert
          </Icon>
        </MDBox>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={closeMenu}>Refresh</MenuItem>
          <MenuItem onClick={closeMenu}>Export</MenuItem>
          <MenuItem onClick={closeMenu}>Settings</MenuItem>
        </Menu>
      </MDBox>
      <MDBox
        mt="-0.3cm"
        sx={{
          "& tbody td": {
            paddingTop: "calc(0.75rem - 0.05cm)",
            paddingBottom: "calc(0.75rem - 0.05cm)",
          },
        }}
      >
        <DataTable
          table={{ columns, rows }}
          showTotalEntries={false}
          isSorted={false}
          noEndBorder
          entriesPerPage={false}
          defaultPageSize={rows.length}
        />
      </MDBox>
    </Card>
  );
}

CycleDP.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string,
  avg_temp_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_tempdp_status: PropTypes.string,
  avg_tempdp_error: PropTypes.bool,
  avg_hum_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_humdp_status: PropTypes.string,
  avg_humdp_error: PropTypes.bool,
  avg_nh3_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_nh3dp_status: PropTypes.string,
  avg_nh3dp_error: PropTypes.bool,
  avg_fcr_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.oneOf([null])]),
  avg_fcrdp_status: PropTypes.string,
  avg_fcrdp_error: PropTypes.bool,
  avg_mortality_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_mortalitydp_status: PropTypes.string,
  avg_mortalitydp_error: PropTypes.bool,
  avg_weight_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_weightdp_status: PropTypes.string,
  avg_weightdp_message: PropTypes.string,
  avg_weightdp_error: PropTypes.bool,
  avg_wtemp_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_wtempdp_status: PropTypes.string,
  avg_wtempdp_error: PropTypes.bool,
  avg_lux_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_luxdp_status: PropTypes.string,
  avg_luxdp_error: PropTypes.bool,
  avg_dark_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_darkdp_status: PropTypes.string,
  avg_darkdp_error: PropTypes.bool,
  env_dark_reading: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  env_dark_status: PropTypes.string,
  env_dark_target: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_ftemp_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_ftempdp_status: PropTypes.string,
  avg_ftempdp_error: PropTypes.bool,
  avg_ph_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_phdp_status: PropTypes.string,
  avg_act_dp: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  avg_actdp_status: PropTypes.string,
  avg_actdp_error: PropTypes.bool,
  avg_act_message: PropTypes.string,
  onMortalityClick: PropTypes.func,
};

export default CycleDP;
